import { useState, useEffect } from "react";
import {
  Gift, Plus, Trash2, X, Copy, Check,
  Search, RefreshCw, Ban, CheckCircle, Clock,
} from "lucide-react";
import { useAdmin } from "../admin-context";

const STATUS_CHIP = {
  active:    { cls: "bg-green-100 text-green-800",  label: "Active"    },
  used:      { cls: "bg-gray-100 text-gray-600",    label: "Used"      },
  expired:   { cls: "bg-red-100 text-red-700",      label: "Expired"   },
  cancelled: { cls: "bg-orange-100 text-orange-700",label: "Cancelled" },
};

const Drawer = ({ open, onClose, title, children }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-40 flex">
      <div className="flex-1 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="w-full max-w-md bg-white h-full shadow-2xl flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="text-lg font-bold text-gray-900">{title}</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full"><X size={18} /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-6">{children}</div>
      </div>
    </div>
  );
};

const CopyButton = ({ text }) => {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <button onClick={copy} className="p-1 hover:bg-gray-100 rounded text-gray-400">
      {copied ? <Check size={13} className="text-green-500" /> : <Copy size={13} />}
    </button>
  );
};

const GiftCardsPage = () => {
  const { giftCards, loadGiftCards, createGiftCard, cancelGiftCard, deleteGiftCard, showToast } = useAdmin();
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [form, setForm] = useState({ amount: "", expires_at: "", code: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => { loadGiftCards(); }, []);

  const handleCreate = async () => {
    if (!form.amount || Number(form.amount) <= 0) return showToast("Enter a valid amount", "error");
    setSaving(true);
    await createGiftCard(form);
    setSaving(false);
    setDrawerOpen(false);
    setForm({ amount: "", expires_at: "", code: "" });
    showToast("Gift card created");
  };

  const handleCancel = async (id) => {
    if (!window.confirm("Cancel this gift card?")) return;
    await cancelGiftCard(id);
    showToast("Gift card cancelled");
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this gift card permanently?")) return;
    await deleteGiftCard(id);
    showToast("Deleted");
  };

  const list = (giftCards || []).filter((c) => {
    if (filter !== "all" && c.status !== filter) return false;
    if (search && !c.code.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const totalValue = (giftCards || []).reduce((s, c) => s + Number(c.initial_amount), 0);
  const activeCount = (giftCards || []).filter((c) => c.status === "active").length;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap gap-3 items-center justify-between">
        <div>
          <h1 className="text-2xl font-extrabold text-gray-900">Gift Cards</h1>
          <p className="text-sm text-gray-500 mt-0.5">Issue and manage gift cards</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => loadGiftCards()} className="flex items-center gap-1.5 px-3 py-2 border rounded-lg text-sm text-gray-600 hover:bg-gray-50">
            <RefreshCw size={15} /> Refresh
          </button>
          <button onClick={() => setDrawerOpen(true)} className="flex items-center gap-1.5 px-4 py-2 bg-orange-500 text-white rounded-lg text-sm font-semibold hover:bg-orange-600">
            <Plus size={16} /> Create Card
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border p-4 flex items-center gap-3">
          <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center"><Gift size={20} className="text-purple-600" /></div>
          <div>
            <p className="text-xs text-gray-500">Total Cards</p>
            <p className="text-2xl font-bold text-gray-900">{(giftCards || []).length}</p>
          </div>
        </div>
        <div className="bg-white rounded-xl border p-4 flex items-center gap-3">
          <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center"><CheckCircle size={20} className="text-green-600" /></div>
          <div>
            <p className="text-xs text-gray-500">Active</p>
            <p className="text-2xl font-bold text-green-700">{activeCount}</p>
          </div>
        </div>
        <div className="bg-white rounded-xl border p-4 flex items-center gap-3">
          <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center"><Gift size={20} className="text-orange-500" /></div>
          <div>
            <p className="text-xs text-gray-500">Total Value Issued</p>
            <p className="text-2xl font-bold text-gray-900">${totalValue.toFixed(2)}</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by code..."
            className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-300" />
        </div>
        {["all", "active", "used", "expired", "cancelled"].map((f) => (
          <button key={f} onClick={() => setFilter(f)}
            className={`px-3 py-2 rounded-lg text-sm font-medium capitalize border transition ${filter === f ? "bg-orange-500 text-white border-orange-500" : "border-gray-200 text-gray-600 hover:bg-gray-50"}`}>
            {f === "all" ? "All" : f}
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                {["Code", "Initial", "Remaining", "Status", "Expires", ""].map((h) => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {list.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-12 text-center text-gray-400">No gift cards found</td></tr>
              ) : list.map((card) => {
                const chip = STATUS_CHIP[card.status] || STATUS_CHIP.active;
                return (
                  <tr key={card.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <span className="font-mono text-xs font-semibold text-gray-800">{card.code}</span>
                        <CopyButton text={card.code} />
                      </div>
                    </td>
                    <td className="px-4 py-3 font-semibold text-gray-900">${Number(card.initial_amount).toFixed(2)}</td>
                    <td className="px-4 py-3 font-semibold text-green-700">${Number(card.remaining_amount).toFixed(2)}</td>
                    <td className="px-4 py-3">
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${chip.cls}`}>{chip.label}</span>
                    </td>
                    <td className="px-4 py-3 text-gray-500 text-xs">
                      {card.expires_at ? new Date(card.expires_at).toLocaleDateString() : "No expiry"}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1 justify-end">
                        {card.status === "active" && (
                          <button onClick={() => handleCancel(card.id)} title="Cancel" className="p-1.5 hover:bg-yellow-50 rounded-lg text-yellow-500">
                            <Ban size={14} />
                          </button>
                        )}
                        <button onClick={() => handleDelete(card.id)} className="p-1.5 hover:bg-red-50 rounded-lg text-red-400">
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create Drawer */}
      <Drawer open={drawerOpen} onClose={() => setDrawerOpen(false)} title="Create Gift Card">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Amount ($) <span className="text-red-500">*</span></label>
            <input type="number" min="1" value={form.amount} onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))}
              placeholder="e.g. 50"
              className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-300" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Custom Code (optional)</label>
            <input type="text" value={form.code} onChange={(e) => setForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))}
              placeholder="Leave blank to auto-generate"
              className="w-full border rounded-lg px-3 py-2 text-sm font-mono uppercase focus:outline-none focus:ring-2 focus:ring-orange-300" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Expiry Date (optional)</label>
            <input type="date" value={form.expires_at} onChange={(e) => setForm((f) => ({ ...f, expires_at: e.target.value }))}
              className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-300" />
          </div>
          <div className="bg-orange-50 rounded-lg p-3 text-xs text-orange-700">
            If no custom code is provided, a unique code will be auto-generated.
          </div>
          <button onClick={handleCreate} disabled={saving}
            className="w-full py-2.5 bg-orange-500 hover:bg-orange-600 text-white font-semibold rounded-lg transition disabled:opacity-60">
            {saving ? "Creating..." : "Create Gift Card"}
          </button>
        </div>
      </Drawer>
    </div>
  );
};

export default GiftCardsPage;
